// src/ExpenseNotesPage.js - NUOVO FILE

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db, storage } from './firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { useUser } from './UserContext';
import { logAction } from './loggingService';
import { FaFilePdf, FaFileImage, FaFileAlt } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import moment from 'moment';
import { ConfirmationModal } from './Modals'; // Riutilizza il modale di conferma
import Select from 'react-select'; // Per il selettore utente (Manager)
import { createNotification } from './notificationService';
import './ExpenseNotesPage.css';


const ExpenseRequestModal = ({ isOpen, onClose, onSave, allStaff, currentUser, isManager, initialUserId }) => {
    const [userId, setUserId] = useState(initialUserId || currentUser.uid);
    const [causale, setCausale] = useState('');
    const [importo, setImporto] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setUserId(initialUserId || currentUser.uid);
    }, [initialUserId, currentUser.uid]);

    const staffOptions = useMemo(() => {
        return allStaff
            .map(s => ({ value: s.id, label: s.name || 'Senza Nome' })) // Fallback nel map
            // --- FIX SICUREZZA QUI ---
            .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
            // -------------------------
    }, [allStaff]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                toast.error("Sono ammessi solo PDF, JPEG o PNG.");
                e.target.value = null;
                setSelectedFile(null);
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleSave = async () => {
        if (!causale.trim() || !importo || !selectedFile) {
            toast.error("Tutti i campi (Causale, Importo, Allegato) sono obbligatori.");
            return;
        }

        const numericImporto = parseFloat(importo);
        if (isNaN(numericImporto) || numericImporto <= 0) {
            toast.error("L'importo deve essere un numero positivo.");
            return;
        }

        setIsSaving(true);
        const targetUser = allStaff.find(s => s.id === userId);
        if (!targetUser) {
            toast.error("Utente non valido.");
            setIsSaving(false);
            return;
        }
        
        let fileUrl = '';
        let storagePath = '';
        
        try {
            // 1. Upload file to Storage
            const fileExtension = selectedFile.name.split('.').pop();
            const uniqueFileName = `${userId}_${moment().format('YYYYMMDDHHmmss')}_${uuidv4()}.${fileExtension}`;
            storagePath = `expense_receipts/${currentUser.centerId}/${uniqueFileName}`;
            const fileRef = ref(storage, storagePath);
            const snapshot = await uploadBytes(fileRef, selectedFile);
            fileUrl = await getDownloadURL(snapshot.ref);

            // 2. Save data to Firestore
            await onSave({
                userId: targetUser.id,
                userName: targetUser.name,
                causale: causale.trim(),
                importo: numericImporto,
                fileUrl: fileUrl,
                storagePath: storagePath,
                centerId: currentUser.centerId,
                status: 'pending'
            });

            toast.success("Nota spesa inviata con successo!");
            onClose();

        } catch (error) {
            console.error("Errore invio nota spesa:", error);
            if (storagePath) {
                // Tentativo di rollback del file se il salvataggio Firestore fallisce
                const fileRef = ref(storage, storagePath);
                deleteObject(fileRef).catch(err => console.error("Rollback file fallito:", err));
            }
            toast.error("Errore nell'invio della nota.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="modal" onMouseDown={onClose}>
            <div className="modal-content" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <h3>Nuova Nota Spesa</h3>

                <div style={{ textAlign: 'left', marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    
                    {isManager && (
                        <div className="form-group">
                            <label>Dipendente</label>
                            <Select 
                                options={staffOptions} 
                                value={staffOptions.find(opt => opt.value === userId)}
                                onChange={opt => setUserId(opt.value)}
                                placeholder="Seleziona dipendente..."
                            />
                        </div>
                    )}
                    
                    <div className="form-group">
                        <label>Causale/Descrizione</label>
                        <input 
                            type="text" 
                            value={causale} 
                            onChange={e => setCausale(e.target.value)} 
                            placeholder="Es. Rimborso benzina, Cena di lavoro..."
                        />
                    </div>
                    <div className="form-group">
                        <label>Importo (€)</label>
                        <input 
                            type="number" 
                            value={importo} 
                            onChange={e => setImporto(e.target.value)} 
                            min="0.01" 
                            step="0.01"
                            placeholder="Es. 45.50"
                        />
                    </div>
                    <div className="form-group">
                        <label>Allegato (Scontrino/Fattura - PDF, JPEG, PNG)</label>
                        <input 
                            type="file" 
                            onChange={handleFileChange} 
                            accept="application/pdf,image/jpeg,image/png"
                        />
                        {selectedFile && <p style={{fontSize: '0.85rem', color: '#198754'}}>File selezionato: {selectedFile.name}</p>}
                    </div>
                </div>

                <div style={{ marginTop: '25px' }}>
                    <button onClick={handleSave} className="modal-button confirm-button" disabled={isSaving}>
                        {isSaving ? 'Invio...' : 'Invia Richiesta'}
                    </button>
                    <button onClick={onClose} className="modal-button cancel-button">Annulla</button>
                </div>
            </div>
        </div>
    );
};

// Componente principale della pagina
function ExpenseNotesPage() {
    const { userProfile } = useUser();
    const permissionLevel = userProfile?.permissions?.expenseNotes || 'none';
    // Manager = accesso completo o semi-completo o proprietario/admin
    const isManager = permissionLevel === 'completo' || userProfile?.role === 'Proprietario' || userProfile?.role === 'Super Admin'; 

    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('pending');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [allStaff, setAllStaff] = useState([]);
    const [confirmModalData, setConfirmModalData] = useState(null);

    // 1. Fetch Staff List (per Manager view e Modale)
    useEffect(() => {
        if (!userProfile || !userProfile.centerId) return;
        const fetchStaff = async () => {
             const staffSnapshot = await getDocs(collection(db, 'centers', userProfile.centerId, 'staff'));
             const staffList = staffSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
             setAllStaff(staffList);
        };
        fetchStaff();
    }, [userProfile]);

    // 2. Fetch Requests in tempo reale
    useEffect(() => {
        if (!userProfile || !userProfile.centerId) return;
        setIsLoading(true);
        const centerId = userProfile.centerId;
        const notesRef = collection(db, 'centers', centerId, 'expenseNotes');
        let q;

        // 🆕 Carichiamo TUTTE le note (senza filtro su status): il filtro per tab si fa lato client.
        // Così i contatori delle tab non attive sono sempre corretti e una nota appena creata
        // compare subito, qualunque tab sia selezionata.
        if (isManager) {
            q = query(notesRef);
        } else {
            q = query(notesRef, where('userId', '==', userProfile.uid));
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            // Ordinamento lato client: un documento appena creato ha requestedAt ancora null
            // (serverTimestamp non è ancora arrivato) e verrebbe escluso da un orderBy nella query.
            const requestsList = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => {
                    const ta = a.requestedAt?.toMillis ? a.requestedAt.toMillis() : Infinity;
                    const tb = b.requestedAt?.toMillis ? b.requestedAt.toMillis() : Infinity;
                    return tb - ta; // desc: i più recenti (e quelli appena creati) in alto
                });
            setRequests(requestsList);
            setIsLoading(false);
        }, (error) => {
            console.error("Errore fetching note spesa:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [userProfile, isManager]);

    // 3. Handler di Salvataggio (da Modale)
    const handleSaveRequest = async (requestData) => {
        // La validazione e l'upload avvengono nel modale
        try {
            await addDoc(collection(db, 'centers', userProfile.centerId, 'expenseNotes'), {
                ...requestData,
                requestedAt: serverTimestamp(),
                status: 'pending'
            });
            
            // Notifica Manager
            if (isManager) {
                 // Non notificare me stesso se sto creando per un altro
            } else {
                // Invia notifica a tutti i manager (come in LeaveManagementPage)
                const staffRef = collection(db, 'centers', userProfile.centerId, 'staff');
                const managerQuery = query(staffRef, where("permissions.payroll", "in", ["completo", "semi-completo"])); // Notifica chi gestisce Paghe
                const managerSnapshot = await getDocs(managerQuery);
                managerSnapshot.forEach(doc => {
                    if (doc.id !== userProfile.uid) { // Non notificare me stesso
                        createNotification(doc.id, userProfile, `ha inviato una richiesta di rimborso spese (€${requestData.importo})`, '/note-spesa');
                    }
                });
            }

        } catch (error) {
            console.error("Errore salvataggio nota spesa:", error);
            toast.error("Si è verificato un errore durante il salvataggio.");
            // Non è necessario un altro toast se la logica di rollback fallisce, il primo basta
        }
    };

    // 4. Handler di Approvazione/Rifiuto (Manager)
    const handleUpdateRequestStatus = async (request, newStatus) => {
        if (!isManager) return;
        
        const noteDocRef = doc(db, 'centers', userProfile.centerId, 'expenseNotes', request.id);
        
        try {
            await updateDoc(noteDocRef, {
                status: newStatus,
                managerId: userProfile.uid,
                managerName: userProfile.name,
                processedAt: serverTimestamp(),
            });
            
            // Notifica l'utente della richiesta
            createNotification(
                request.userId, userProfile,
                `la tua richiesta di rimborso (€${request.importo}) è stata ${newStatus === 'approved' ? 'approvata' : 'rifiutata'}`,
                '/note-spesa'
            );
            
            await logAction(userProfile, `ha ${newStatus} la nota spesa di ${request.userName} (€${request.importo})`);
            toast.success(`Richiesta ${newStatus} con successo!`);
            
        } catch (error) {
            console.error(`Errore aggiornamento stato ${newStatus}:`, error);
            toast.error(`Errore durante l'aggiornamento dello stato.`);
        } finally {
            setConfirmModalData(null);
        }
    };

    // 🆕 Note della tab attualmente selezionata (requests contiene tutte le note)
    const visibleRequests = requests.filter(r => r.status === activeTab);

    const getFileIcon = (fileUrl) => {
        if (fileUrl.includes('.pdf')) return <FaFilePdf style={{ color: '#dc3545', fontSize: '1.2rem' }} />;
        if (fileUrl.includes('.jpg') || fileUrl.includes('.jpeg') || fileUrl.includes('.png')) return <FaFileImage style={{ color: '#0d6efd', fontSize: '1.2rem' }} />;
        return <FaFileAlt style={{ color: '#6c757d', fontSize: '1.2rem' }} />;
    };

    return (
        <div className="expense-notes-container">
            {isModalOpen && (
                <ExpenseRequestModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveRequest}
                    allStaff={allStaff}
                    currentUser={userProfile}
                    isManager={isManager}
                    initialUserId={isManager && allStaff.length > 0 ? allStaff.find(s => s.id === userProfile.uid)?.id || allStaff[0].id : userProfile.uid}
                />
            )}
            
            {confirmModalData && (
                 <ConfirmationModal
                     title={confirmModalData.title}
                     onConfirm={confirmModalData.onConfirm}
                     onClose={() => setConfirmModalData(null)}
                 >
                     {confirmModalData.children}
                 </ConfirmationModal>
            )}

            <div className="expense-notes-header">
                <h1>Gestione Note Spesa</h1>
                <button 
                    className="new-request-btn" 
                    onClick={() => setIsModalOpen(true)}
                >
                    + Nuova Nota Spesa
                </button>
            </div>

            <div className="tabs-nav">
                 <button onClick={() => setActiveTab('pending')} className={activeTab === 'pending' ? 'active' : ''}>In Attesa ({requests.filter(r => r.status === 'pending').length})</button>
                 <button onClick={() => setActiveTab('approved')} className={activeTab === 'approved' ? 'active' : ''}>Approvate ({requests.filter(r => r.status === 'approved').length})</button>
                 <button onClick={() => setActiveTab('denied')} className={activeTab === 'denied' ? 'active' : ''}>Rifiutate ({requests.filter(r => r.status === 'denied').length})</button>
             </div>
             
             <div className="requests-grid">
                 {isLoading ? <p>Caricamento richieste...</p> : visibleRequests.length === 0 ? (
                     <p className="no-requests-message">Nessuna nota spesa trovata in questa categoria.</p>
                 ) : visibleRequests.map(req => {
                     const userColor = allStaff.find(s => s.id === req.userId)?.color || '#ccc';
                     const statusClass = `status-${req.status}`;
                     
                     return (
                         <div key={req.id} className={`leave-card-item ${statusClass}`} style={{ borderLeftColor: userColor }}>
                            <div className="leave-card-header">
                                 <div>
                                    <p className="user-name">{req.userName}</p>
                                    <p className="request-type">{req.causale}</p>
                                 </div>
                                 <div className="status-action-group">
                                     <span className="status-badge" style={{ backgroundColor: userColor, color: 'white' }}>€ {req.importo.toFixed(2)}</span>
                                     <span className={`status-badge ${statusClass}`}>{req.status === 'pending' ? 'In Attesa' : req.status === 'approved' ? 'Approvata' : 'Rifiutata'}</span>
                                 </div>
                            </div>
                            <div className="leave-card-body">
                                 <p className="request-dates">
                                     Inviata il {moment(req.requestedAt?.toDate()).format('DD/MM/YYYY')}
                                 </p>
                                <p className="request-notes">{req.causale}</p>
                                <a href={req.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', fontWeight: 'bold' }}>
                                     {getFileIcon(req.fileUrl)} Visualizza Allegato
                                </a>
                            </div>
                            {isManager && activeTab === 'pending' && (
                                <div className="leave-card-footer">
                                    <button 
                                        className="approve-btn" 
                                        onClick={() => setConfirmModalData({
                                            title: `Approvare €${req.importo.toFixed(2)} per ${req.userName}?`,
                                            onConfirm: () => handleUpdateRequestStatus(req, 'approved'),
                                            children: <p>Causale: {req.causale}</p>
                                        })}
                                    >
                                        Approva
                                    </button>
                                    <button 
                                        className="deny-btn" 
                                        onClick={() => setConfirmModalData({
                                            title: `Rifiutare €${req.importo.toFixed(2)} per ${req.userName}?`,
                                            onConfirm: () => handleUpdateRequestStatus(req, 'denied'),
                                            children: <p>Causale: {req.causale}</p>
                                        })}
                                    >
                                        Rifiuta
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                 })}
             </div>
        </div>
    );
}

export default ExpenseNotesPage;